import React, { useEffect, useState } from "react";
import { Col, Row, Select } from "antd";
import { Controller, useForm } from "react-hook-form";
import styles from "./add-edit-departments.module.css";
import CustomDrawer from "../../../../../../../../../common/custom-drawer/custom-drawer";
import CustomTextfield from "../../../../../../../../../common/input/custom-textfield/custom-textfield";
import CustomButton from "../../../../../../../../../common/custom-button/custom-button";
import { toast } from "react-toastify";
import { COLOR_CODE } from "../../../../../../../../../constants/common";
import CustomSelect from "../../../../../../../../../common/input/custom-select/custom-select";
import { fetchTeacherList } from "../../../../../../../../../services/teachers";
import { createDepartment, updateDepartment } from "../../../../../../../../../services/school_info";
import { refetchSchool } from "../../../../../../../../../utils/common";

const { Option } = Select;

const AddEditDepartments = ({ open, onClose, data }) => {
  const [loading, setLoading] = useState(false);
  const [teacherList, setTeacherList] = useState([]);
  const { control, getValues, reset, trigger } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      colour_code: "",
      hod_teacher_id: "",
    },
  });
  
  useEffect(() => {
    if (!open) return;
    getTeacherList();
  }, [open]);

  useEffect(() => {
    if (data?.id) {
      reset({
        name: data?.name,
        colour_code: data?.colour_code,
        hod_teacher_id: data?.hod_teacher_id,
      });
    } else {
      reset({
        name: "",
        colour_code: "",
        hod_teacher_id: "",
      });
    }
  }, [data]);

  const getTeacherList = async () => {
    try {
      setLoading(true);
      const response = await fetchTeacherList();
      if (response?.status) {
        setTeacherList(response?.data?.data);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    try {
      const isValid = await trigger();
      if (!isValid) {
        return;
      }

      const formValues = getValues();
      const payload = {
        name: formValues?.name,
        colour_code: formValues?.colour_code,
        hod_teacher_id: formValues?.hod_teacher_id,
      };
      if (data?.id) await updateDepartment(data?.id, payload);
      else await createDepartment(payload);
      toast.success(`Department ${data?.id ? "edited" : "created"} successfully!`);
      refetchSchool();
      handleClose();
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.message || `Failed to ${data?.id ? "edit" : "create"} department`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <CustomDrawer
      title={`${data?.id ? 'Edit' : 'Add New'} Department`}
      open={open}
      onClose={handleClose}
      className={styles.drawer}
      footer={
        <div className={styles.footerWrapper}>
          <CustomButton
            onClick={handleClose}
            title="Back"
            variant="outlined"
            className={styles.footerBackButton}
          />
          <CustomButton
            title={`${data?.id ? "Edit" : "Add New"} Department`}
            size="large"
            onClick={handleSubmit}
          />
        </div>
      }
      width="40vw"
      drawerStyles={{ body: { padding: "50px" } }}
    >
      <Row gutter={[0, 20]}>
        <Col span={24}>
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomTextfield
                id="name"
                label="Department Name"
                showRequired
                labelColor="#171717"
                placeholder="Enter Department Name"
                size="large"
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
              />
            )}
          />
        </Col>

        <Col span={24}>
          <Controller
            name="colour_code"
            control={control}
            rules={{ required: true }}
            render={({
              field: { onChange, value },
              fieldState: { error },
            }) => (
              <CustomSelect
                id="colour_code"
                label="Select Department Color"
                showRequired
                labelColor="#171717"
                placeholder="--Select--"
                showSearch
                size="large"
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
              >
                {COLOR_CODE?.map((hex) => (
                  <Option key={hex} value={hex}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div
                        style={{
                          height: 20,
                          width: 20,
                          borderRadius: 2,
                          backgroundColor: `${hex}1A`,
                          border: `1px solid ${hex}66`,
                        }}
                      />
                      {hex}
                    </div>
                  </Option>
                ))}
              </CustomSelect>
            )}
          />
        </Col>

        <Col span={24}>
          <Controller
            name="hod_teacher_id"
            control={control}
            render={({
              field: { onChange, value },
              fieldState: { error },
            }) => (
              <CustomSelect
                id="hod_teacher_id"
                label="Heads of Departments"
                labelColor="#171717"
                placeholder="--Select--"
                showSearch
                size="large"
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
              >
                {teacherList?.map((option, index) => (
                  <Option key={`${option?.id}-${index}`} value={option?.id}>
                    {option?.full_name}
                  </Option>
                ))}
              </CustomSelect>
            )}
          />
        </Col>
      </Row>
    </CustomDrawer>
  );
};

export default AddEditDepartments;
